import React, { useState, useEffect, useMemo, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

import Layout from "../../../../Layout/Layout";
import CustomerConstants from "../../../../common/CustomerConstants";
import { SearchField } from "../../../../Components/formElements/SearchField";
import { TableStatusField } from "../../../../Components/formElements/TableStatusField";
import { formatDateWithAMPM } from "../../../../common/Validations";
import {
  getErfServiceFormList,
  setErfServiceDocumentNumber,
} from "../../../../redux/feature/customer_app/customerSlice";
import MultiColumnFilter from "../../../../Components/MultiColumnFilter/MultiColumnFilter";
import ViewServiceDetailsModal from "./ViewServiceDetailsModal";
import ServiceVisitInvoiceModal from "./ServiceVisitInvoiceModal";

const BOOLEAN_FILTER_KEYS = ["shouldCollectMoney"];
const EXACT_MATCH_KEYS = [
  "serviceStatus",
  "jobDoneStatus",
  "financeStatus",
  "activeDocumentType",
  "serviceCategory",
  "erfType",
  "privilegeCase",
];

const ServiceErfList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  localStorage.setItem("menu", "Equipment Rent");
  localStorage.setItem("submenu", "ERF Service List");

  // ── Redux ──────────────────────────────────────────────────────────────
  const { data: reduxData, pagination } =
    useSelector((state) => state?.customer?.erfServiceForm) || {};
  const userData = useSelector((state) => state?.user);

  // ── Role detection ─────────────────────────────────────────────────────
  const isServiceExecutive = useMemo(
    () =>
      userData?.data?.roles?.some((r) => r.roleId === "SERVICE-EXECUTIVE") ||
      false,
    [userData],
  );
  const isFinanceExecutive = useMemo(
    () =>
      userData?.data?.roles?.some((r) => r.roleId === "FI-EXECUTIVE") || false,
    [userData],
  );
  const currentUserId = userData?.data?.userId || "";

  // ── Table state ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [perPage, setPerPage] = useState(
    CustomerConstants.ROW_REPORT_PER_PAGE[0],
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTxt, setSearchTxt] = useState("");
  const [isCardVisible, setCardVisible] = useState(true);
  const [filterShow, setFilterShow] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  // ── Modals ─────────────────────────────────────────────────────────────
  const [showViewServiceModal, setShowViewServiceModal] = useState(false);
  const [viewServiceData, setViewServiceData] = useState(null);
  const [getModalEditInvoice, setModalEditInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  // ── Filter normalization ───────────────────────────────────────────────
  const normalizeFilters = (filters) =>
    filters.map((condition) => {
      const key = Object.keys(condition)[0];

      if (BOOLEAN_FILTER_KEYS.includes(key)) {
        const regexStr = condition[key]?.$regex || "";
        const raw = regexStr.replace(/\.\*/g, "").toLowerCase();
        if (raw === "yes" || raw === "true") return { [key]: true };
        if (raw === "no" || raw === "false") return { [key]: false };
        return condition;
      }

      if (EXACT_MATCH_KEYS.includes(key)) {
        const regexStr = condition[key]?.$regex || "";
        if (regexStr) {
          return { [key]: regexStr.replace(/\.\*/g, "").trim() };
        }
        return condition;
      }

      return condition;
    });

  // ── Fetch ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(
    async (page = 1, size = 10, search = "", filters = []) => {
      setLoading(true);
      try {
        const filterConditions = [];

        // SE-scoping: SE sees only their assigned services
        if (isServiceExecutive && currentUserId) {
          filterConditions.push({ serviceProviderUserId: currentUserId });
        }

        if (filters.length > 0) {
          filterConditions.push(...filters);
        }

        const payload = {
          queryName: "ERF_Service_List",
          page,
          per_page: size,
          searchTerm: search,
          timeZone: "Asia/Calcutta",
          rowFilter:
            filterConditions.length > 0 ? { $and: filterConditions } : {},
        };

        await dispatch(getErfServiceFormList(payload));
      } catch (error) {
        console.error("Error fetching service list", error);
        toast.error("Error loading records");
      } finally {
        setLoading(false);
      }
    },
    [dispatch, isServiceExecutive, currentUserId],
  );

  // ── Trigger fetch when userData / page / filters change ───────────────
  useEffect(() => {
    if (userData?.data?.userId) {
      fetchData(currentPage, perPage, searchTxt, activeFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, userData, activeFilters]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const detailedPage = (row) => {
    dispatch(
      setErfServiceDocumentNumber({
        documentNumber: row?.documentNumber,
        erfDocumentNumber: row?.equipmentRentNr,
      }),
    );
    navigate(CustomerConstants.CUSTOMER_V5_MR_ERF_SERVICE_DETAIL_PAGE);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleMultiColumnFilter = (filterResult) => {
    const filtersArray = Array.isArray(filterResult)
      ? filterResult
      : filterResult?.$and || [];
    setActiveFilters(normalizeFilters(filtersArray));
    setCurrentPage(1);
  };

  const handleFilterCancel = () => {
    setFilterShow(false);
    setActiveFilters([]);
    setCurrentPage(1);
  };

  const showEditInvoice = (row) => {
    setInvoiceData(row);
    setModalEditInvoice(true);
  };

  const hideInvoiceModal = () => {
    setModalEditInvoice(false);
    setInvoiceData(null);
  };

  // eslint-disable-next-line
  const handleViewServiceDetails = (row) => {
    setViewServiceData(row);
    setShowViewServiceModal(true);
  };

  // ── Filter config ──────────────────────────────────────────────────────
  const filterColumns = [
    { id: "documentNumber", name: "Service No", selectorKey: "documentNumber" },
    { id: "equipmentRentNr", name: "ERF No", selectorKey: "equipmentRentNr" },
    { id: "patientName", name: "Patient Name", selectorKey: "patientName" },
    { id: "salePoint", name: "Sale Point", selectorKey: "salePoint" },
    {
      id: "serviceStatus",
      name: "Service Status",
      selectorKey: "serviceStatus",
      dropDown: true,
      staticOptions: [
        "Created",
        "Assigned",
        "In Progress",
        "Completed",
        "Cancelled",
      ],
    },
    {
      id: "jobDoneStatus",
      name: "Job Status",
      selectorKey: "jobDoneStatus",
      dropDown: true,
      staticOptions: ["Pending", "Done", "Not Done"],
    },
    {
      id: "tallyStatusIndicator",
      name: "Tally Status",
      selectorKey: "tallyStatusIndicator",
      dropDown: true,
      staticOptions: ["Done", "Pending"],
    },
    {
      id: "financeCollectionConfirmation",
      name: "Collection By Finance",
      selectorKey: "financeCollectionConfirmation",
      dropDown: true,
      staticOptions: ["Confirmed", "Not Confirmed"],
    },
    {
      id: "activeDocumentType",
      name: "Doc Type",
      selectorKey: "activeDocumentType",
      dropDown: true,
      staticOptions: ["ERF", "Extension"],
    },
    {
      id: "serviceCategory",
      name: "Category",
      selectorKey: "serviceCategory",
      dropDown: true,
      staticOptions: ["Service", "Replacement"],
    },
    {
      id: "serviceProviderName",
      name: "Service Executive",
      selectorKey: "serviceProviderName",
    },
    {
      id: "serviceDate",
      name: "Service Date",
      selectorKey: "serviceDate",
      month: true,
    },
    {
      id: "createdAt",
      name: "Created Date",
      selectorKey: "createdAt",
      month: true,
    },
    {
      id: "shouldCollectMoney",
      name: "Payment Required",
      selectorKey: "shouldCollectMoney",
      dropDown: true,
      staticOptions: ["Yes", "No"],
    },
  ];

  // ── Columns ────────────────────────────────────────────────────────────
  const columns = [
    {
      name: "Created Date",
      cell: (row) => formatDateWithAMPM(row.createdAt),
      width: "200px",
    },
    {
      name: "Service No",
      cell: (row) => (
        <button className="btnInfo" onClick={() => detailedPage(row)}>
          {row.documentNumber || "--"}
        </button>
      ),
      width: "210px",
    },
    {
      name: "ERF No",
      selector: (row) => row.equipmentRentNr,
      width: "210px",
    },
    {
      name: "Service Status",
      cell: (row) =>
        row?.serviceStatus ? (
          <TableStatusField status={row.serviceStatus} />
        ) : (
          <span className="text-muted small">—</span>
        ),
      width: "140px",
    },
    {
      name: "Job Status",
      cell: (row) => {
        const s = row?.jobDoneStatus;
        if (!s) return <span className="text-muted">--</span>;
        const color =
          s === "Done" ? "#28a745" : s === "Not Done" ? "#dc3545" : "#ffc107";
        return (
          <span
            className="badge"
            style={{
              backgroundColor: color,
              color: s === "Pending" ? "black" : "white",
              padding: "6px 10px",
              borderRadius: "10px",
            }}
          >
            {s}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: "Assigned SE",
      cell: (row) => (
        <div>
          <p className="fw-bold mb-0">{row?.serviceProviderName || "--"}</p>
          <small className="text-muted">{row?.serviceProviderCode || ""}</small>
        </div>
      ),
      width: "160px",
    },
    {
      name: "Service Date",
      cell: (row) => (
        <div>
          <p className="mb-0">{row?.serviceDate || "--"}</p>
          <small className="text-muted">{row?.serviceTime || ""}</small>
        </div>
      ),
      width: "130px",
    },
    {
      name: "Items",
      cell: (row) => {
        const items = row?.serviceRequiredItems || [];
        const total = items.length;
        const repl = items.filter(
          (i) => i.serviceAction === "Replacement",
        ).length;
        const svc = items.filter((i) => i.serviceAction === "Service").length;
        if (total === 0) return <span className="text-muted">--</span>;
        return (
          <div>
            <p className="fw-bold mb-0">{total} item(s)</p>
            <small className="text-muted">
              {svc > 0 && `${svc} Service`}
              {svc > 0 && repl > 0 && " · "}
              {repl > 0 && `${repl} Replace`}
            </small>
          </div>
        );
      },
      width: "140px",
    },
    {
      name: "Invoice",
      cell: (row) => {
        // const isAllowed = row?.serviceStatus === "Completed";

        const isAllowed =
          row?.serviceStatus === "Completed" &&
          row?.financeCollectionConfirmation === "Confirmed";

        return row?.invoiceCreated ? (
          <i
            className="bi bi-receipt"
            style={{
              fontSize: 22,
              color: "#2d7a79",
              cursor: "pointer",
            }}
            onClick={() => showEditInvoice(row)}
          />
        ) : (
          <button
            className="btn btnTable"
            disabled={!isAllowed}
            onClick={() => showEditInvoice(row)}
            title={!isAllowed ? "Available after service is Completed" : ""}
          >
            Generate
          </button>
        );
      },
      width: "150px",
      omit: !isFinanceExecutive,
    },
    {
      name: "ERF Type",
      cell: (row) => (
        <div>
          <TableStatusField status={row.erfType} />
          <p className="fw-bold mt-1 mb-0">{row?.privilegeCase}</p>
        </div>
      ),
      width: "180px",
    },
    {
      name: "Patient Details",
      cell: (row) => (
        <div>
          <p className="fw-bold mb-0">{row.patientName}</p>
          <p className="mb-0">{row?.mobileNr}</p>
          <small className="text-muted">{row.billingAddress}</small>
        </div>
      ),
      width: "200px",
    },
    {
      name: "Doc Type",
      cell: (row) => (
        <span
          className="badge"
          style={{
            backgroundColor:
              row?.activeDocumentType === "Extension" ? "#fff3cd" : "#cce5ff",
            color:
              row?.activeDocumentType === "Extension" ? "#856404" : "#004085",
            padding: "5px 10px",
            borderRadius: "10px",
          }}
        >
          {row?.activeDocumentType || "ERF"}
        </span>
      ),
      width: "120px",
    },
    {
      name: "Payment",
      cell: (row) => {
        if (!row?.shouldCollectMoney)
          return <span className="text-muted small">—</span>;
        const target = Number(row?.amountToCollect || 0);
        const collected = Number(row?.totalCollected || 0);
        const done = target > 0 && collected >= target;
        return (
          <div>
            <p
              className="fw-bold mb-0"
              style={{ color: done ? "#28a745" : "#dc3545" }}
            >
              ₹ {collected.toFixed(2)} / ₹ {target.toFixed(2)}
            </p>
            <small className="text-muted">
              {done ? "Collected" : "Pending"}
            </small>
          </div>
        );
      },
      width: "180px",
    },
    {
      name: "Tally Status",
      selector: (row) => row.tallyStatusIndicator,
      cell: (row) => (
        <span
          style={{
            fontWeight: "bold",
            color:
              row.tallyStatusIndicator === "Done"
                ? "#2563eb"
                : row.tallyStatusIndicator === "Pending"
                  ? "#dc2626"
                  : "#6b7280",
          }}
        >
          {row.tallyStatusIndicator || "-"}
        </span>
      ),
      wrap: true,
      width: "120px",
    },
    {
      name: "Collect. By Finance",
      selector: (row) => row?.financeCollectionConfirmation,
      cell: (row) => (
        <span
          style={{
            fontWeight: "bold",
            color:
              row?.financeCollectionConfirmation === "Confirmed"
                ? "#2563eb"
                : row?.financeCollectionConfirmation === "Not Confirmed"
                  ? "#dc2626"
                  : "#6b7280",
          }}
        >
          {row?.financeCollectionConfirmation || "-"}
        </span>
      ),
      wrap: true,
      width: "180px",
    },
    {
      name: "Sale Point",
      cell: (row) => (
        <div>
          <p className="mb-0">{row.salePoint}</p>
          <small className="text-muted">{row.createdBy}</small>
        </div>
      ),
      width: "180px",
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <Row>
              <Col xs={12} md={8} lg={8}>
                <h2 className="mainTitle">ERF Service Form List</h2>
                <p className="subText">
                  List of service requests across ERFs and Extensions
                </p>
              </Col>
              <Col xs={12} md={3} lg={3} className="mt-3">
                <SearchField
                  name="searchKey"
                  value={searchTxt}
                  onChange={(e) => setSearchTxt(e.target.value)}
                  onClick={() =>
                    fetchData(1, perPage, searchTxt, activeFilters)
                  }
                  placeholder="Search..."
                />
              </Col>
              <Col xs={4} md={1} lg={1}>
                <span
                  className="filterIcon"
                  onClick={() => setFilterShow(!filterShow)}
                >
                  <i className="bi bi-filter" />
                </span>
              </Col>
            </Row>
            <hr />

            {filterShow && (
              <Row>
                <Col xs={12}>
                  <MultiColumnFilter
                    columns={filterColumns}
                    data={reduxData}
                    onFilter={handleMultiColumnFilter}
                    onCancel={handleFilterCancel}
                    output="and"
                  />
                </Col>
              </Row>
            )}

            <div className="accordion">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible ? "bi-chevron-down" : "bi-chevron-right"
                        }`}
                        onClick={() => setCardVisible(!isCardVisible)}
                      />
                    </Col>
                    <Col xs={1} className="text-center logoBtn">
                      <span className="iconLogo">SL</span>
                    </Col>
                    <Col xs={10}>
                      <span className="headerTitle">Service List</span>
                      <span className="subHeaderTitle">
                        View Details and Status
                      </span>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    {loading ? (
                      <div className="text-center py-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        />
                        <p className="mt-3 text-muted">
                          Loading service records...
                        </p>
                      </div>
                    ) : (
                      <DataTable
                        title=""
                        columns={columns}
                        data={reduxData || []}
                        pagination
                        paginationServer
                        paginationPerPage={perPage}
                        paginationTotalRows={pagination?.total || 0}
                        paginationRowsPerPageOptions={
                          CustomerConstants.ROW_REPORT_PER_PAGE
                        }
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        highlightOnHover
                        pointerOnHover
                        persistTableHead={false}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <ServiceVisitInvoiceModal
        show={getModalEditInvoice}
        onHide={hideInvoiceModal}
        serviceData={invoiceData}
        onSuccess={() => {
          hideInvoiceModal();
          fetchData(currentPage, perPage, searchTxt, activeFilters);
        }}
      />

      <ViewServiceDetailsModal
        show={showViewServiceModal}
        onHide={() => setShowViewServiceModal(false)}
        data={viewServiceData}
        onSuccess={() =>
          fetchData(currentPage, perPage, searchTxt, activeFilters)
        }
      />
    </main>
  );
};

export default ServiceErfList;
